import HBBoardNav from "@/components/HBBoardNav";
import HBCategoryNav from "@/components/HBCategoryNav";
import { AB_DISCOVERY_NAV } from "@/constants/ABMng";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { shouldGoToFollowPage } from "@/utils/getHomeUrl";
import useABTest from "@/utils/hooks/useABtest";
import useUrlState from "@/utils/useUrlState";
import { omit } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";

const FILTER_CATEGORIES = ["funny", "beauty"]; // 分类过滤掉美女和搞笑

export default function BoardNav() {
  const router = useRouter();
  const { user, categories } = useAppStore((s) => ({
    user: s.user,
    categories: s.categories,
  }));

  // AB 实验判断
  const discoveryNavTest = useABTest(AB_DISCOVERY_NAV);
  const showBoardNav = discoveryNavTest.new;

  const [queryParam] = useUrlState();
  let { categoryId = null, tabId = "pins", board = null } = router.query;
  const { pathname } = router;

  // 根据路由确定当前选中的值
  const currentValue = useMemo(() => {
    if (pathname === "/discovery/boards/[board]" && board) {
      return board; // 画板详情页，返回画板 ID
    }
    return categoryId; // 其他页面返回分类 ID
  }, [pathname, board, categoryId]);

  const routeMap = {
    follow: "/follow",
    discovery: "/discovery",
    guide: "/guide",
  };

  const initialValue = useMemo(() => {
    const currentPath = router.pathname;
    if (
      currentPath.includes(routeMap.follow) ||
      currentPath.includes(routeMap.guide)
    ) {
      return "follow";
    }
    return null; // 默认值
  }, [router.pathname, routeMap.follow, routeMap.guide]);

  const extraOption = useMemo(() => {
    return user ? { text: "关注", value: "follow" } : undefined;
  }, [user]);

  // 分类导航的回调函数
  const onChangeCategoryNav = useCallback(
    (category) => {
      // 如果点击了同一个tab，则不进行路由跳转，只触发数据刷新事件
      const newCategoryId = category?.value || null;
      if (newCategoryId === "follow" && pathname === "/follow") {
        // 点击关注tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.FOLLOW_PIN_LIST_RESET));
        return;
      } else if (newCategoryId === null && pathname === "/discovery") {
        // 点击发现tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      } else if (
        newCategoryId === categoryId &&
        pathname === "/discovery/[categoryId]"
      ) {
        // 点击同一个分类，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      }

      if (category && category.value === "follow") {
        // 检查用户是否应该跳转到关注页
        if (user && shouldGoToFollowPage(user)) {
          router.push("/follow");
        } else {
          router.push("/guide");
        }
        return;
      }
      if (tabId === "pins") {
        const param = omit(queryParam, ["categoryId"]);
        router.push({
          pathname: category ? `/discovery/${category.value}` : "/discovery",
          query: {
            ...param,
          },
        });
      } else {
        router.push({
          pathname: category
            ? `/discovery/${category.value}/${tabId}`
            : `/discovery/all/${tabId}`,
        });
      }
    },
    [queryParam, router, tabId, user, categoryId, pathname]
  );

  // 画板导航的回调函数
  const onChangeBoardNav = useCallback(
    (board) => {
      // 如果点击了同一个tab，则不进行路由跳转，只触发数据刷新事件
      const newBoardId = board?.value || null;
      if (newBoardId === "follow" && pathname === "/follow") {
        // 点击关注tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.FOLLOW_PIN_LIST_RESET));
        return;
      } else if (newBoardId === null && pathname === "/discovery") {
        // 已经在发现页，点击发现tab，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      } else if (
        newBoardId === router.query.board &&
        pathname === "/discovery/boards/[board]"
      ) {
        // 点击同一个画板，触发数据刷新事件
        window.dispatchEvent(new Event(EVENT.DISCOVERY_PIN_LIST_RESET));
        return;
      }

      if (board && board.value === "follow") {
        // 检查用户是否应该跳转到关注页
        if (user && shouldGoToFollowPage(user)) {
          router.push("/follow");
        } else {
          router.push("/guide");
        }
        return;
      }

      // 画板导航默认都是针对 pins，不需要考虑 tabId
      if (board?.value) {
        router.push(`/discovery/boards/${board.value}`);
      } else {
        router.push("/discovery");
      }
    },
    [router, user, pathname]
  );

  // 根据 AB 实验结果渲染不同的导航组件
  if (showBoardNav) {
    // 新版画板导航 - 只有登录用户才显示画板导航，否则显示基础的发现/关注导航
    return (
      <HBBoardNav
        allText={"发现"}
        extraOption={extraOption}
        baseHref="/discovery"
        onChange={onChangeBoardNav}
        value={currentValue || initialValue}
      />
    );
  } else {
    // 原版分类导航
    return (
      <HBCategoryNav
        allText={"发现"}
        extraOption={extraOption}
        options={categories}
        filterValues={FILTER_CATEGORIES}
        baseHref="/discovery"
        onChange={onChangeCategoryNav}
        value={categoryId || initialValue}
      />
    );
  }
}
